// UPDATED SharePointSidebar.tsx with simplified targeting context and full area clickable

import React, { useRef, useEffect } from 'react';
import { MoreVertical, CirclePlus, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { SharePointData } from '../../types/sharepoint.types';

interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  sharepointId: number;
}

interface SharePointSidebarProps {
  sharepoints: SharePointData[];
  setSharepoints: React.Dispatch<React.SetStateAction<SharePointData[]>>;
  selectedSharePoint: number;
  setSelectedSharePoint: (id: number) => void;
  systemConfig: { Id: string; ConfigValue: string; }[];
  contextMenu: ContextMenu;
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenu>>;
  onAddNewSharePoint: () => void;
  onMoveSharePoint: (sharepointId: number, direction: 'up' | 'down') => void;
  onDeleteSharePoint: (sharepointId: number) => void;
  onNameChange: (event: React.ChangeEvent<HTMLInputElement>, sharepointId: number) => void;
  onDragStart: (e: React.DragEvent, position: number) => void;
  onDragEnter: (e: React.DragEvent, position: number) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onContextMenu: (e: React.MouseEvent, sharepointId: number) => void;
}

const SharePointSidebar: React.FC<SharePointSidebarProps> = ({
  sharepoints,
  selectedSharePoint,
  setSelectedSharePoint,
  systemConfig,
  contextMenu,
  setContextMenu,
  onAddNewSharePoint,
  onMoveSharePoint,
  onDeleteSharePoint,
  onNameChange,
  onDragStart,
  onDragEnter,
  onDragLeave,
  onDragEnd,
  onDragOver,
  onContextMenu
}) => {
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const maxAllowedSites = Number(systemConfig.find(item => item.Id === 'va_max_allowed_sharepoint_sites')?.ConfigValue) || 5;

  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  // Simplified renderTargetingContext function
  const renderTargetingContext = (sharepoint: SharePointData) => {
    const hasUrlAnalysis = sharepoint?.url_analysis;
    const urlType = hasUrlAnalysis?.url_type;
    
    // Don't show context for site-level connections
    if (!hasUrlAnalysis || urlType === 'site') {
      return null;
    }

    // Helper function to find list name by ID
    const findListName = (listId: string): string | null => {
      if (!sharepoint.sharepointItems?.lists || !Array.isArray(sharepoint.sharepointItems.lists)) {
        return null;
      }
      
      const targetList = sharepoint.sharepointItems.lists.find(list => list.id === listId);
      if (targetList) {
        return targetList.title || targetList.display_name || targetList.name || null;
      }
      return null;
    };

    // Helper function to find library name by ID  
    const findLibraryName = (libraryId: string): string | null => {
      if (!sharepoint.sharepointItems?.document_libraries || !Array.isArray(sharepoint.sharepointItems.document_libraries)) {
        return null;
      }
      
      const targetLibrary = sharepoint.sharepointItems.document_libraries.find(
        library => library.id === libraryId || library.drive_id === libraryId
      );
      if (targetLibrary) {
        return targetLibrary.title || targetLibrary.display_name || targetLibrary.name || null;
      }
      return null;
    };

    // Determine what to display based on targeting type
    let targetName = '';
    let typeLabel = '';
    let icon = null;

    switch (urlType) {
      case 'list': {
        const targetListId = hasUrlAnalysis?.target_list_id;
        targetName = targetListId ? (findListName(targetListId) || 'List') : 'List';
        typeLabel = 'List';
        
        // SharePoint LIST - Table/grid style icon
        icon = (
          <svg className="h-3 w-3 mr-1" viewBox="0 0 16 16" fill="currentColor">
            <path fillRule="evenodd" d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v2h14V2a1 1 0 0 0-1-1H2zM1 5v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V5H1z"/>
            <path d="M1 6h4v2H1V6zm5 0h4v2H6V6zm5 0h4v2h-4V6zM1 9h4v2H1V9zm5 0h4v2H6V9zm5 0h4v2h-4V9z"/>
          </svg>
        );
        break;
      }
        
      case 'library': {
        const targetLibraryId = hasUrlAnalysis?.target_library_id;
        targetName = targetLibraryId ? (findLibraryName(targetLibraryId) || 'Library') : 'Library';
        typeLabel = 'Library';

        // SharePoint LIBRARY - Document with folded corner icon
        icon = (
          <svg className="h-3 w-3 mr-1" viewBox="0 0 16 16" fill="currentColor">
            <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zM4 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4z"/>
            <path d="M9.5 3V1L14 4.5h-2A1.5 1.5 0 0 1 9.5 3z"/>
            <path d="M5 6.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5zM5 8.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5zM5.5 10a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3z"/>
          </svg>
        );
        break;
      }
        
      case 'folder': {
        const folderPath = hasUrlAnalysis?.target_folder_path;
        const folderName = folderPath ? folderPath.split('/').pop() || folderPath : 'Folder';
        targetName = folderName;
        typeLabel = 'Folder';

        // SharePoint FOLDER - Professional folder icon
        icon = (
          <svg className="h-3 w-3 mr-1" viewBox="0 0 16 16" fill="currentColor">
            <path d="m.5 3 .04.87a1.99 1.99 0 0 0-.342 1.311l.637 7A2 2 0 0 0 2.826 14h10.348a2 2 0 0 0 1.991-1.819l.637-7A1.99 1.99 0 0 0 15.46 3.87L15.5 3h-3.672a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 8.172 1H2.5a.5.5 0 0 0 0 1h5.672a1 1 0 0 1 .707.293L9.586 3H.5z"/>
          </svg>
        );
        break;
      }
        
      default:
        return null;
    }

    return (
      <div className="flex items-center text-xs text-gray-600 mt-1 ml-6">
        {icon}
        <span className="truncate">
          {typeLabel}
        </span>
      </div>
    );
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu, setContextMenu]);

  const sortedSharepoints = [...sharepoints].sort((a, b) => a.order - b.order);

  return (
    <>
      {/* Left Sidebar */}
      <div className="w-64 border-r border-gray-200 overflow-y-auto">
        {sortedSharepoints.map((sharepoint, index) => (
          <div
            key={sharepoint.id}
            draggable
            onDragStart={(e) => onDragStart(e, index)}
            onDragEnter={(e) => onDragEnter(e, index)}
            onDragLeave={onDragLeave}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
            onClick={() => setSelectedSharePoint(sharepoint.id)}
            className={`p-4 cursor-pointer hover:bg-gray-50 
            ${selectedSharePoint === sharepoint.id ? 'bg-gray-100' : ''}
            transition-opacity duration-200`}
          >
            {/* Main row with SharePoint icon, name and context menu */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                <img src="/sharepoint-logo.png" alt="SharePoint Logo" className="h-5 w-5" />
                <span className="text-sm flex-1 truncate" title={sharepoint?.sharepoint_name}>
                  {sharepoint.sharepoint_name || `SharePoint Site ${sharepoint.order}`}
                </span>
              </div>
              <button
                className="hover:bg-gray-100 p-1 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  onContextMenu(e, sharepoint.id);
                }}
              >
                <MoreVertical className="h-4 w-4 text-gray-400" />
              </button>
            </div>
            
            {/* Simplified targeting context */}
            {renderTargetingContext(sharepoint)}
          </div>
        ))}
        
        {sharepoints.length < maxAllowedSites && (
          <div className="p-4">
            <button
              onClick={onAddNewSharePoint}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              <CirclePlus className="h-6 w-6 text-gray-500" />
            </button>
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white shadow-lg rounded-lg py-1 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => {
              onMoveSharePoint(contextMenu.sharepointId, 'up');
              closeContextMenu();
            }}
          >
            <ChevronUp className="h-4 w-4" />
            <span>Move Up</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => {
              onMoveSharePoint(contextMenu.sharepointId, 'down');
              closeContextMenu();
            }}
          >
            <ChevronDown className="h-4 w-4" />
            <span>Move Down</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2 text-red-600"
            onClick={() => {
              onDeleteSharePoint(contextMenu.sharepointId);
              closeContextMenu();
            }}
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      )}
    </>
  );
};

export default SharePointSidebar;